import React, { useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { DebounceInput } from 'react-debounce-input';
import {
  IncidentObjective,
  useCreateIncidentObjective,
  useEditIncidentObjective,
  useIncidentObjectives,
} from './hooks/incidentObjectiveHooks';
import PratusInput from 'components/DesignSystems/New/PratusInput';
import StylishNewComboBox, {
  type ComboBoxOption,
} from 'components/DesignSystems/New/StylishNewComboBox';
import { OBJECTIVES_LIBRARY } from './planningView/widgets/ostLibraryData';

import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import { INCIDENT } from './ICSConstants';
import {
  OBJECTIVE_TYPE_OPERATIONAL,
  incidentObjectiveTypeSegments,
  normalizeIncidentObjectiveType,
} from './incidentObjectiveTypes';

// Local type for table rows. We only need core fields for this UI.
type ObjectiveRow = Pick<
  IncidentObjective,
  'objective_id' | 'name' | 'archived' | 'strategies' | 'type'
> & {
  id: string; // stable id for table (objective_id for existing, custom for new)
  _isNew?: boolean;
  description?: string;
};

const NEW_ROW_ID = '__new__';

// PratusTable forwards TanStack Table `column.size` to React `style.width`; unitless numbers are CSS pixels.
const OBJECTIVES_TABLE_CHECKBOX_COL_WIDTH_PX = 64;
const OBJECTIVES_TABLE_TYPE_COL_WIDTH_PX = 144;

type IncidentObjectivesTableProps = {
  readOnly?: boolean;
};

const IncidentObjectivesTable: React.FC<IncidentObjectivesTableProps> = ({
  readOnly = false,
}) => {
  // TODO: No stable sort right now. adding new items to the top of the list causes confusion.
  const { data: liveObjectives = [] } = useIncidentObjectives(INCIDENT, (i) =>
    i.filter((o) => !o.archived)
  );

  const createObjectiveMutation = useCreateIncidentObjective();
  const editObjectiveMutation = useEditIncidentObjective();

  const [query, setQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newRowType, setNewRowType] = useState(OBJECTIVE_TYPE_OPERATIONAL);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const onAddClick = () => {
    setIsAdding(true);
  };

  const onCancelAdd = () => {
    setIsAdding(false);
    setNewRowType(OBJECTIVE_TYPE_OPERATIONAL);
  };

  const saveObjective = useMemo(
    () =>
      async (
        row: ObjectiveRow,
        updates: Partial<Pick<IncidentObjective, 'name' | 'archived' | 'type'>>
      ) => {
        const isCreate =
          !row.objective_id || row._isNew || row.id === NEW_ROW_ID;
        const trimmedName = updates.name?.trim();

        if (isCreate) {
          if (!trimmedName) return;
          await createObjectiveMutation.mutateAsync({
            name: trimmedName,
            description: undefined,
            type: newRowType,
          });
          setIsAdding(false);
          setNewRowType(OBJECTIVE_TYPE_OPERATIONAL);
          return;
        }

        const nextType =
          updates.type !== undefined
            ? updates.type
            : normalizeIncidentObjectiveType(row.type);

        editObjectiveMutation.mutate({
          name: trimmedName ?? row.name,
          archived: updates.archived ?? row.archived,
          objective_id: row.objective_id,
          strategies: row.strategies || [],
          type: nextType,
          description: row.description,
        });
        setEditingId(null);
      },
    [createObjectiveMutation, editObjectiveMutation, newRowType]
  );

  const filtered = useMemo(() => {
    const base = liveObjectives;
    if (!query) return base;
    const q = query.toLowerCase();
    return base.filter((o) => (o.name || '').toLowerCase().includes(q));
  }, [liveObjectives, query]);

  const tableData: ObjectiveRow[] = useMemo(() => {
    const rows = filtered.map<ObjectiveRow>((o) => ({
      id: String(o.objective_id || o.id),
      objective_id: o.objective_id,
      name: o.name,
      archived: o.archived,
      strategies: (o as any).strategies || [],
      type: normalizeIncidentObjectiveType(o.type),
      description: (o as any).description,
    }));
    if (!isAdding) return rows;
    return [
      {
        id: NEW_ROW_ID,
        objective_id: '' as any,
        name: '',
        archived: false,
        _isNew: true,
        strategies: [],
        type: newRowType,
      },
      ...rows,
    ];
  }, [filtered, isAdding, newRowType]);

  const selectedRows = useMemo(
    () =>
      tableData.filter(
        (row) => !row._isNew && !!row.objective_id && selectedIds.has(row.id)
      ),
    [selectedIds, tableData]
  );

  const deleteSelected = async () => {
    if (!selectedRows.length) return;

    await Promise.all(
      selectedRows.map((row) =>
        editObjectiveMutation.mutateAsync({
          name: row.name,
          archived: true,
          ['objective_id']: row.objective_id,
          strategies: row.strategies || [],
          type: normalizeIncidentObjectiveType(row.type),
          description: row.description,
        })
      )
    );

    setSelectedIds(new Set());
  };

  const visibleIds = useMemo(
    () => tableData.filter((r) => !r._isNew).map((r) => r.id),
    [tableData]
  );

  const columnHelper = createColumnHelper<ObjectiveRow>();
  const columns = useMemo<ColumnDef<ObjectiveRow, any>[]>(
    () => [
      columnHelper.display({
        id: 'checkbox',
        size: OBJECTIVES_TABLE_CHECKBOX_COL_WIDTH_PX,
        header: () => (
          <StylishNewCheckbox
            id="select-all"
            checked={
              visibleIds.length > 0 &&
              visibleIds.every((id) => selectedIds.has(id))
            }
            onClick={(e) => {
              e.stopPropagation();
              const allSelected =
                visibleIds.length > 0 &&
                visibleIds.every((id) => selectedIds.has(id));
              setSelectedIds((prev) => {
                if (allSelected) {
                  // Unselect all visible
                  return new Set(
                    [...prev].filter((id) => !visibleIds.includes(id))
                  );
                }
                // Select all visible
                return new Set([...prev, ...visibleIds]);
              });
            }}
          />
        ),
        cell: (info) => {
          const row = info.row.original as ObjectiveRow;
          const rowId = row.id;
          const checked = selectedIds.has(rowId);
          return (
            <StylishNewCheckbox
              id={`select-${rowId}`}
              checked={checked}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  if (checked) next.delete(rowId);
                  else next.add(rowId);
                  return next;
                });
              }}
            />
          );
        },
        enableSorting: false,
        meta: { customSize: true },
      }),
      columnHelper.display({
        id: 'objectiveType',
        header: () => <span>Type</span>,
        size: OBJECTIVES_TABLE_TYPE_COL_WIDTH_PX,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div
              className="btn-group btn-group-sm"
              role="group"
              aria-label="Objective type"
            >
              {incidentObjectiveTypeSegments.map((seg) => {
                const active =
                  normalizeIncidentObjectiveType(row.type) === seg.value;
                return (
                  <OverlayTrigger
                    key={seg.value}
                    placement="top"
                    overlay={(props) => (
                      <Tooltip id={`inc-obj-type-${seg.value}`} {...props}>
                        {seg.tooltip}
                      </Tooltip>
                    )}
                  >
                    <button
                      type="button"
                      className={classNames(
                        'btn btn-outline-secondary incident-objectives-type-seg px-2 py-0',
                        { active }
                      )}
                      aria-pressed={active}
                      disabled={readOnly}
                      onClick={() => {
                        if (readOnly) return;
                        if (row._isNew) {
                          setNewRowType(seg.value);
                          return;
                        }
                        if (active) return;
                        void saveObjective(row, { type: seg.value });
                      }}
                    >
                      {seg.abbrev}
                    </button>
                  </OverlayTrigger>
                );
              })}
            </div>
          );
        },
        enableSorting: false,
        meta: { customSize: true },
      }),
      columnHelper.accessor('name', {
        id: 'name',
        header: () => <span>Objective</span>,
        enableSorting: true,
        cell: (info) => {
          const row = info.row.original;
          const isEditing = editingId === row.id;
          if (row._isNew) {
            return (
              <div className="d-flex align-items-center flex-wrap gap-2">
                <StylishNewComboBox
                  classes="flex-grow-1"
                  options={OBJECTIVES_LIBRARY}
                  placeholder="Select or create Objective..."
                  customOptionLabel="+ Create New"
                  customInputMaxLength={200}
                  onChange={(newValue) => {
                    const option = newValue as ComboBoxOption | null;
                    if (option) {
                      void saveObjective(row, {
                        name: option.label,
                      });
                    }
                  }}
                />
                <StylishNewButton
                  className="button--icon"
                  onClick={onCancelAdd}
                  title="Cancel"
                >
                  <SharedIcon iconName="close" size="sm" />
                </StylishNewButton>
              </div>
            );
          }
          if (isEditing) {
            return (
              <PratusInput
                defaultValue={row.name || ''}
                onSave={(val) => saveObjective(row, { name: val })}
                onCancel={() => setEditingId(null)}
                maxLength={200}
                autoFocus
              />
            );
          }
          return <span>{row.name}</span>;
        },
        meta: { align: 'left' },
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span style={{ width: 48, display: 'inline-block' }} />,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const isEditing = editingId === row.id;
          if (readOnly) return null;
          return (
            <div className="d-flex justify-content-end gap-2 pe-2">
              {isEditing ? null : (
                <button
                  className="btn btn-link p-0"
                  title="Edit"
                  onClick={() => {
                    setEditingId(row.id);
                  }}
                >
                  <SharedIcon iconName="edit" size="sm" classes="text-muted" />
                </button>
              )}
              <button
                className="btn btn-link p-0"
                title="Delete"
                onClick={async () => {
                  await saveObjective(row, { archived: true });
                }}
              >
                <SharedIcon iconName="delete" size="sm" classes="text-muted" />
              </button>
            </div>
          );
        },
        meta: { align: 'right' },
      }),
    ],
    [
      columnHelper,
      editingId,
      readOnly,
      saveObjective,
      selectedIds,
      visibleIds,
      setNewRowType,
    ]
  );

  return (
    <div className="pratus-objectives">
      <div className="d-flex align-items-center justify-content-between pb-2">
        <div className="d-flex align-items-center flex-grow-1 gap-2">
          <div className="txt-md">Incident Objectives</div>
          <div className="d-flex align-items-center rounded-1 border border-neutral-700">
            <SharedIcon iconName="search" size="sm" classes="ms-1 text-muted" />
            <DebounceInput
              debounceTimeout={250}
              className="form-control border-0 py-1 ps-1"
              placeholder="Search objectives..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        {!readOnly && (
          <StylishNewButton
            className="button--primary d-inline-flex align-items-center ms-2"
            onClick={onAddClick}
            disabled={isAdding}
          >
            <SharedIcon iconName="add" size="sm" classes="me-1" /> Add Objective
          </StylishNewButton>
        )}
      </div>

      <div className="rounded-1 bg-neutral-800 p-0">
        {!readOnly && selectedRows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-2 px-2 pt-2">
            <div>{selectedRows.length} selected</div>
            <StylishNewButton
              className="button--error"
              onClick={(e) => {
                e.stopPropagation();
                void deleteSelected();
              }}
              disabled={editObjectiveMutation.isPending}
            >
              <SharedIcon iconName="delete" classes="me-1" /> Delete Selected
            </StylishNewButton>
          </div>
        )}
        <PratusTable
          className={'layout-auto pratus-table'}
          columns={columns}
          data={tableData}
          getRowId={(row) => row.id}
          responsive={false}
          noDataIndication={
            <div className="d-flex justify-content-center flex-column align-items-center p-3">
              <div>No objectives yet</div>
              <div>{`Click "Add Objective" to create your first objective`}</div>
            </div>
          }
        />
      </div>

      <div className="text-muted small mt-2">{filtered.length} objectives</div>
    </div>
  );
};

export default IncidentObjectivesTable;
